// Formatadores compactos do Laboratório fee-based (BRL abreviado, bps→%, pp).
// Portados do protótipo. Reusam toMoney do @utils/Format onde cabe.

import { toMoney } from '@utils/Format'
import { regimeDoCanal } from '@modules/fee-fixo/constants/baldes'

// Reais completo (sem casas decimais).
export const fmtBRL = (value) => {
  return toMoney(Number(value) || 0, 0)
}

// Reais abreviado (>= 1 bi → "R$ X bi"; >= 1 mi → "R$ X mi"; >= 1 mil → "R$ X mil").
//
// DÍVIDA-design S-5: faltava o degrau de bilhão — acima de 1e9 o valor
// continuava caindo no ramo "mi" (ex.: "R$ 2300,0 mi"), enquanto o eixo do
// gráfico ao lado (formatLines/abreviarValorMask, @utils/GraficoData e
// @utils/Format) já abrevia em "B" a partir do mesmo patamar — o mesmo
// número aparecia em escalas diferentes no mesmo card.
//
// `abreviarValor`/`abreviarMonetario` (@utils/Format) NÃO servem aqui: eles
// resolvem o degrau de bilhão, mas com sufixo em outra convenção ("B"/"M"/"K",
// maiúsculo, sem espaço-vírgula) — trocar `fmtBRLk` por eles mudaria o
// contrato de exibição já testado e em uso em todo o laboratório fee-based
// (fmtBRLk é consumido por fee-lab-kpi-strip, -regime-cards,
// -composicao-custo, -evolucao-patrimonio, -saved-scenarios, -baldes-custo e
// a view de simulação) — de "R$ X mi"/"R$ X mil" para "R$ X M"/"R$ X K", e
// exigiria realinhar precisão (abreviarValor usa 1 casa para toda faixa;
// fmtBRLk usa 0 para mil e 1 para milhão/bilhão) e o fallback de zero
// (abreviarMonetario devolve '-' para value 0; fmtBRLk devolve "R$ 0"). Por
// isso a extensão é local, preservando o sufixo "bi" já coerente com "mi"/
// "mil".
export const fmtBRLk = (value) => {
  const v = Number(value) || 0
  const abs = Math.abs(v)
  if (abs >= 1e9) return 'R$ ' + roundStr(v / 1e9, 1) + ' bi'
  if (abs >= 1e6) return 'R$ ' + roundStr(v / 1e6, 1) + ' mi'
  if (abs >= 1e3) return 'R$ ' + roundStr(v / 1e3, 0) + ' mil'
  return fmtBRL(v)
}

// bps inteiro com sufixo.
export const bps = (value) => {
  return Math.round(Number(value) || 0) + ' bps'
}

// bps com N casas e sinal (contribuições pequenas do card de composição, onde
// arredondar em % viraria "-0,00%" — ilegível). Sinal usa o mesmo minus
// U+2212 de signPP/signPctF. Arredonda via Math.round num fator de 10
// (não delega direto ao toFixed do commaFixed) porque toFixed herda o erro
// de representação binária do IEEE754 — ex.: (0.15).toFixed(1) === '0.1',
// não '0.2' — e o fixture normativo (REQUIREMENTS §3.4) exige que
// bpsN(-0.15) produza '−0,2 bps' (Retirement, cenário default).
export const bpsN = (value, precision = 1) => {
  const v = Number(value) || 0
  const factor = Math.pow(10, precision)
  const rounded = Math.round(Math.abs(v) * factor) / factor
  const sign = v < 0 && rounded !== 0 ? '−' : ''
  return sign + commaFixed(rounded, precision) + ' bps'
}

// bps → percentual a.a. (117 bps → "1,17%").
export const asPct = (value) => {
  return commaFixed((Number(value) || 0) / 100, 2) + '%'
}

// bps → percentual com N casas.
export const asPctN = (value, precision = 2) => {
  return commaFixed((Number(value) || 0) / 100, precision) + '%'
}

// Fração → percentual (0.34 → "34%").
export const pct = (value, precision = 0) => {
  return commaFixed((Number(value) || 0) * 100, precision) + '%'
}

// Diferença em pontos percentuais com sinal (bps → pp).
export const signPP = (value) => {
  const v = (Number(value) || 0) / 100
  const sign = v >= 0 ? '+' : '−'
  return sign + commaFixed(Math.abs(v), 2) + ' pp'
}

// Fração → percentual com sinal ("+12%").
export const signPctF = (value) => {
  const v = (Number(value) || 0) * 100
  const sign = v >= 0 ? '+' : '−'
  return sign + commaFixed(Math.abs(v), 0) + '%'
}

// Receita/custo anual em reais a partir de bps sobre o AuC.
export const rPerYear = (value, auc) => {
  return ((Number(auc) || 0) * (Number(value) || 0)) / 10000
}

/**
 * Ganho anual do profissional em reais (bps sobre o AuC), com clamp em 0 —
 * nunca grava/exibe valor negativo (Tier C). É a fonte do KPI "Ganho do
 * &lt;profissional&gt; · ano" e do `valor` que os modais gravam na oportunidade
 * (individual e em lote).
 *
 * **O CANAL escolhe a perna.** O motor publica as duas
 * (`advGain: { fee, cons }`) e esta função lia sempre `fee`: com o Consultor
 * CVM selecionado, a tela creditava ao profissional o ganho do fee-based
 * (R$ 1.090,79 no cenário default) enquanto os cards ao lado publicavam a
 * receita do consultor — 60 bps contra 10,72 do comissionado, ou seja
 * R$ 4.928,09 de ganho. Foi a contradição que o owner viu em 2026-08-03.
 *
 * `canal` omitido cai no canal que o PRÓPRIO resultado publica (`simulate`
 * grava `canal`), e só então no default declarado. Assim um consumidor que
 * esqueça o argumento lê o canal daquele resultado em vez de uma perna fixa —
 * que é como este defeito nasceu.
 *
 * @param {Object|null} resultado Saída de `simulate` (ou o `resultados` de um
 *   cenário salvo).
 * @param {number} auc Patrimônio sobre o qual os bps incidem.
 * @param {string} [canal] Canal do cenário; ver `constants/baldes.js`.
 */
export const calcularGanhoAnualDoProfissional = (resultado, auc, canal) => {
  const advGain = (resultado && resultado.advGain) || {}
  const perna = regimeDoCanal(canal || (resultado && resultado.canal))
  const ganho = advGain[perna] ? advGain[perna] : 0
  return Math.max(0, rPerYear(ganho, auc))
}

// ---- helpers internos ----
const commaFixed = (value, precision) => {
  return (Number(value) || 0).toFixed(precision).replace('.', ',')
}

const roundStr = (value, precision) => {
  const s = (Number(value) || 0).toFixed(precision)
  return s.replace('.', ',')
}
